import { useCallback, useEffect, useRef } from "react";
import { SLIDES_CONTAINER_ID, useSlidesLibraryStoreShallow } from "@modules/slides-library/shared";

const SEARCH_CLEAR_RENDER_DELAY_MS = 250;
const POST_ACTIVATE_RENDER_DELAY_MS = 50;
const SCROLL_OFFSET_PX = 8;
const SCROLL_RESET_TIMEOUT_MS = 800;

export const useScrollToCategory = () => {
  const { isInSearch, setSearch, setActiveTemplateCategoryId, setIsManualScrolling } =
    useSlidesLibraryStoreShallow((s) => ({
      isInSearch: !!s.search,
      setSearch: s.setSearch,
      setActiveTemplateCategoryId: s.setActiveTemplateCategoryId,
      setIsManualScrolling: s.setIsManualScrolling,
    }));

  const scrollResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(
    () => () => {
      if (scrollResetTimeoutRef.current) {
        clearTimeout(scrollResetTimeoutRef.current);
        scrollResetTimeoutRef.current = null;
      }
    },
    [],
  );

  return useCallback(
    async (id: string) => {
      if (isInSearch) {
        setSearch("");
        await new Promise((resolve) =>
          setTimeout(resolve, SEARCH_CLEAR_RENDER_DELAY_MS),
        );
      }
      setActiveTemplateCategoryId(id);
      // Small delay to ensure content is rendered after search clear
      await new Promise((resolve) =>
        setTimeout(resolve, POST_ACTIVATE_RENDER_DELAY_MS),
      );
      const container = document.getElementById(SLIDES_CONTAINER_ID);
      const viewport = container?.querySelector("[data-radix-scroll-area-viewport]") as HTMLElement | null;
      if (!viewport) return;
      const element = viewport.querySelector(
        `[data-category-id="category:${id}"]`,
      ) as HTMLElement;
      if (!element) return;
      setIsManualScrolling(true);
      const elementTop = element.getBoundingClientRect().top;
      const viewportTop = viewport.getBoundingClientRect().top;
      const scrollTarget =
        viewport.scrollTop + (elementTop - viewportTop) + SCROLL_OFFSET_PX;
      viewport.scrollTo({ top: Math.max(0, scrollTarget), behavior: "smooth" });
      if (scrollResetTimeoutRef.current) {
        clearTimeout(scrollResetTimeoutRef.current);
      }
      scrollResetTimeoutRef.current = setTimeout(() => {
        setIsManualScrolling(false);
        scrollResetTimeoutRef.current = null;
      }, SCROLL_RESET_TIMEOUT_MS);
    },
    [isInSearch, setSearch, setActiveTemplateCategoryId, setIsManualScrolling],
  );
};
